import { useMemo } from "react";
import { useUserQuery } from "@/api/v1/user-api";
import {
  getPlanLimits,
  type PlanLimits,
  type Feature,
  hasFeatureAccess,
  getLimit,
  isUnlimited,
} from "@/api/v1/subscription-types";

/**
 * Hook to get the current user's subscription plan limits
 */
export function useSubscriptionLimits() {
  const { data: user, isLoading } = useUserQuery();

  const limits: PlanLimits = useMemo(() => {
    return getPlanLimits(user?.subscription_plan_slug);
  }, [user?.subscription_plan_slug]);

  const planSlug = user?.subscription_plan_slug || "free";
  const isActive = user?.subscription_status
    ? ["active", "trialing", "past_due", "incomplete"].includes(
        user.subscription_status
      )
    : false;

  return {
    limits,
    planSlug,
    isActive,
    isLoading,
  };
}

/**
 * Hook to check if user has access to a specific feature
 * @param feature - The feature to check
 * @param currentCount - Optional current count of the feature (e.g., current number of geofences)
 */
export function useFeatureAccess(
  feature: Feature,
  currentCount?: number
) {
  const { limits, isActive, isLoading } = useSubscriptionLimits();

  const hasAccess = useMemo(() => {
    if (!isActive) {
      return false;
    }
    return hasFeatureAccess(limits, feature, currentCount);
  }, [limits, feature, currentCount, isActive]);

  const limit = useMemo(() => {
    return getLimit(limits, feature);
  }, [limits, feature]);

  const isUnlimitedFeature = useMemo(() => {
    return isUnlimited(limit);
  }, [limit]);

  const remaining = useMemo(() => {
    if (isUnlimitedFeature || currentCount === undefined) {
      return null; // null means unlimited or unknown
    }
    return Math.max(0, limit - currentCount);
  }, [limit, currentCount, isUnlimitedFeature]);

  return {
    hasAccess,
    limit,
    isUnlimited: isUnlimitedFeature,
    remaining,
    isLoading,
  };
}
